/**
 * RequestReplacementModal
 * -----------------------
 * Staff-facing modal for proactively requesting coverage on one of their
 * own future shifts. Submits to the additive endpoint:
 *   POST /api/shift-replacements/employee-request
 *
 * Manager must approve before the existing replacement engine kicks in.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { shiftReplacementAPI } from '../lib/api';

const REASONS = [
  { value: 'sick',                 label: 'Sick' },
  { value: 'family_emergency',     label: 'Family emergency' },
  { value: 'appointment',          label: 'Appointment' },
  { value: 'transportation_issue', label: 'Transportation issue' },
  { value: 'personal',             label: 'Personal' },
  { value: 'other',                label: 'Other' },
];

export default function RequestReplacementModal({ open, onClose, shift, onSubmitted }) {
  const [reason, setReason] = useState('sick');
  const [details, setDetails] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!shift) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acknowledged) {
      toast.error('Please confirm the acknowledgement to submit');
      return;
    }
    setSubmitting(true);
    try {
      await shiftReplacementAPI.employeeRequest({
        shift_id: shift.id,
        reason,
        details: details.trim() || null,
      });
      toast.success('Replacement request submitted');
      setReason('sick');
      setDetails('');
      setAcknowledged(false);
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const shiftLabel =
    shift.date && shift.start_time
      ? `${shift.date} · ${shift.start_time}${shift.end_time ? `–${shift.end_time}` : ''}`
      : '—';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose?.()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="replacement-request-modal"
      >
        <DialogHeader>
          <DialogTitle data-testid="replacement-request-title">
            Replacement Request
          </DialogTitle>
          <DialogDescription>
            Request coverage for your shift on{' '}
            <span className="font-medium text-slate-700">{shiftLabel}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="replacement-reason">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="replacement-reason" data-testid="replacement-reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="replacement-details">
              Optional details <span className="text-slate-400 font-normal">(visible to your manager)</span>
            </Label>
            <Textarea
              id="replacement-details"
              rows={3}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Add any context your manager should know."
              maxLength={2000}
              data-testid="replacement-details-input"
            />
          </div>

          <label
            htmlFor="replacement-ack"
            className="flex items-start gap-2 cursor-pointer text-sm text-slate-700"
            data-testid="replacement-ack-label"
          >
            <input
              id="replacement-ack"
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
              data-testid="replacement-ack-checkbox"
            />
            <span>
              I understand manager approval may be required.
            </span>
          </label>

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="replacement-submit-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1.5" />
              )}
              Submit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
